import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash, AlertCircle, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Task, TaskStatus } from '../../types/task';
import { format } from 'date-fns';

interface TaskTableProps {
  tasks: Task[];
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  onStatusChange: (task: Task, status: TaskStatus) => void;
}

export const TaskTable: React.FC<TaskTableProps> = ({ 
  tasks, 
  onEdit, 
  onDelete,
  onStatusChange
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Fonction de navigation vers la page de détails d'une tâche - mise à jour du chemin
  const navigateToTaskDetails = (taskId: number | undefined) => {
    if (taskId !== undefined) {
      navigate(`/app/tasks/${taskId}`);
    }
  };
  
  // Helper function to format due date with warning if past due
  const renderDueDate = (task: Task) => {
    if (!task.dueDate) return <span className="text-muted-foreground">-</span>;
    
    const dueDate = new Date(task.dueDate);
    const isOverdue = task.status !== 'DONE' && dueDate < new Date();
    
    return (
      <div className="flex items-center gap-1.5">
        <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
          {format(dueDate, 'PP')}
        </span>
        {isOverdue && (
          <AlertCircle className="h-4 w-4 text-red-500" />
        )}
      </div>
    );
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: TaskStatus): string => {
    switch (status) {
      case TaskStatus.DONE: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case TaskStatus.IN_PROGRESS: return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case TaskStatus.TODO: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('task.title')}</TableHead>
            <TableHead>{t('task.client')}</TableHead>
            <TableHead>{t('task.assignedTo')}</TableHead>
            <TableHead>{t('task.state')}</TableHead>
            <TableHead>{t('task.dueDate')}</TableHead>
            <TableHead className="text-right">{t('task.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center h-32">
                {t('task.noTasks')}
              </TableCell>
            </TableRow>
          ) : (
            tasks.map((task) => (
              <TableRow 
                key={task.id} 
                className="hover:bg-muted/50 cursor-pointer"
                onClick={(e) => {
                  // Eviter la navigation si on clique sur un élément interactif (boutons, select)
                  if (
                    (e.target as HTMLElement).closest('button') || 
                    (e.target as HTMLElement).closest('select') ||
                    (e.target as HTMLElement).closest('[data-radix-select-trigger]')
                  ) {
                    return;
                  }
                  task.id !== undefined && navigateToTaskDetails(task.id);
                }}
              >
                <TableCell className="font-medium">{task.title}</TableCell>
                <TableCell>
                  {task.assignedTo?.name || task.user?.name || 'Non assigné'}
                </TableCell>
                <TableCell>
                  {task.assignedTo ? task.assignedTo.name : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={task.status}
                    onValueChange={(value: TaskStatus) => onStatusChange(task, value)}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue>
                        <Badge className={getStatusBadgeVariant(task.status)}>
                          {t(`task.status.${task.status.toLowerCase()}`)}
                        </Badge>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TaskStatus.TODO}>
                        <Badge className={getStatusBadgeVariant(TaskStatus.TODO)}>
                          {t('task.status.todo')}
                        </Badge>
                      </SelectItem>
                      <SelectItem value={TaskStatus.IN_PROGRESS}>
                        <Badge className={getStatusBadgeVariant(TaskStatus.IN_PROGRESS)}>
                          {t('task.status.in_progress')}
                        </Badge>
                      </SelectItem>
                      <SelectItem value={TaskStatus.DONE}>
                        <Badge className={getStatusBadgeVariant(TaskStatus.DONE)}>
                          {t('task.status.done')}
                        </Badge>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{renderDueDate(task)}</TableCell>
                <TableCell className="text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(task)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(task)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => task.id !== undefined && navigateToTaskDetails(task.id)}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
